// src/pages/Admin/PagesPersonnalisees/EditPageModal.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { FaSave, FaTimes, FaLink, FaFileAlt } from "react-icons/fa";
import Button from "./../../../components/Button/Button";
import { updatePage } from "./../../../services/pagesPersonnaliseesServices";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// Quill toolbar
const modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    ["link", "image", "video"],
    ["clean"],
  ],
};

const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "color",
  "background",
  "script",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
];

const slugify = (s = "") =>
  s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

export default function EditPageModal({ isOpen, onClose, onSuccess, page }) {
  const [formData, setFormData] = useState({
    titre: "",
    slug: "",
    introduction: "",
    actif: true,
  });
  const [slugTouched, setSlugTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Hydrate à l’ouverture + quand page change
  useEffect(() => {
    if (!isOpen) return;
    if (page) {
      setFormData({
        titre: page.titre || "",
        slug: page.slug || "",
        introduction: page.introduction || "",
        actif: !!page.actif,
      });
      setSlugTouched(false);
    }
  }, [isOpen, page]);

  // ESC pour fermer
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // Auto-slug tant que l’utilisateur n’a pas touché le champ slug
  useEffect(() => {
    if (!isOpen) return;
    if (!slugTouched) {
      setFormData((prev) => ({ ...prev, slug: slugify(prev.titre || "") }));
    }
  }, [isOpen, formData.titre, slugTouched]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "slug") setSlugTouched(true);
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleIntroductionChange = (value) => {
    setFormData((prev) => ({ ...prev, introduction: value }));
  };

  const canSubmit = useMemo(() => {
    return (
      !submitting &&
      !!page?.id &&
      formData.titre.trim().length > 0 &&
      formData.slug.trim().length > 0
    );
  }, [page?.id, formData.titre, formData.slug, submitting]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!canSubmit) return;
      setSubmitting(true);
      try {
        const payload = {
          id: page.id,
          titre: formData.titre.trim(),
          slug: slugify(formData.slug),
          introduction: formData.introduction,
          actif: !!formData.actif,
        };
        await updatePage(payload);
        toast.success("Page mise à jour avec succès.");
        onSuccess?.();
      } catch (error) {
        console.error("Erreur lors de la mise à jour de la page :", error);
        toast.error("Erreur lors de la mise à jour de la page.");
      } finally {
        setSubmitting(false);
      }
    },
    [canSubmit, formData, onSuccess, page?.id]
  );

  const handleCancel = () => {
    onClose?.();
    // Reset aux valeurs reçues
    if (page) {
      setFormData({
        titre: page.titre || "",
        slug: page.slug || "",
        introduction: page.introduction || "",
        actif: !!page.actif,
      });
      setSlugTouched(false);
    }
  };

  if (!isOpen || !page) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay cliquable */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={handleCancel}
        aria-hidden
      />

      {/* Modal */}
      <div className="relative w-full max-w-3xl overflow-hidden rounded-2xl shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
        {/* Header dégradé */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
          <div className="flex items-center justify-center gap-2">
            <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center text-white">
              <FaFileAlt />
            </div>
            <h3 className="text-white text-lg sm:text-xl font-bold text-center uppercase">
              Modifier la Page
            </h3>
          </div>
          <p className="mt-2 text-center text-white/90 text-xs sm:text-[13px]">
            Mettez à jour le titre, le chemin (slug) et l’introduction riche si nécessaire.
          </p>
        </div>

        {/* Body (scrollable) */}
        <form onSubmit={handleSubmit} className="p-5 sm:p-6 overflow-y-auto max-h-[78vh]">
          {/* Titre */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
              Titre de la page
            </label>
            <input
              type="text"
              name="titre"
              placeholder="Titre"
              value={formData.titre}
              onChange={handleChange}
              className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              required
            />
          </div>

          {/* Slug */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
              Chemin (slug)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                <FaLink />
              </span>
              <input
                type="text"
                name="slug"
                placeholder="chemin-de-la-page"
                value={formData.slug}
                onChange={handleChange}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                required
              />
            </div>
            <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
              Chemin généré : <span className="font-mono">/page/{slugify(formData.slug)}</span>
            </p>
          </div>

          {/* Introduction */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
              Introduction (optionnelle)
            </label>
            <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700">
              <ReactQuill
                value={formData.introduction}
                onChange={handleIntroductionChange}
                modules={modules}
                formats={formats}
                className="[&_.ql-container]:min-h-[160px] [&_.ql-container]:max-h-[300px] [&_.ql-container]:overflow-y-auto"
              />
            </div>
          </div>

          {/* Actif */}
          <label className="inline-flex items-center gap-2 mb-2">
            <input
              type="checkbox"
              name="actif"
              checked={formData.actif}
              onChange={handleChange}
              className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-400 dark:bg-slate-900 dark:border-slate-700"
            />
            <span className="text-sm text-slate-700 dark:text-slate-200">Page active</span>
          </label>

          {/* Actions */}
          <div className="mt-5 flex justify-end gap-2">
            <Button
              text="Annuler"
              icon={FaTimes}
              onClick={handleCancel}
              type="button"
              className="!bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 !rounded-xl gap-2"
            />
            <Button
              text={submitting ? "Enregistrement…" : "Enregistrer"}
              icon={FaSave}
              type="submit"
              disabled={!canSubmit}
              className={`!rounded-xl gap-2 ${!canSubmit ? "opacity-60 pointer-events-none" : ""}`}
            />
          </div>
        </form>
      </div>
    </div>
  );
}
